import { Redirect, Route, useLocation, Switch } from "react-router-dom";
import {
  IonTabs,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonLabel,
} from "@ionic/react";
import { House, ListChecks, Settings, Trophy } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import TodayPage from "../pages/TodayPage";
import HomeStatusPage from "../pages/HomeStatusPage";
import AreaDetailPage from "../pages/AreaDetailPage";
import LeaderboardPage from "../pages/LeaderboardPage";
import SettingsPage from "../pages/SettingsPage";
import EffortSchedulePage from "../pages/settings/EffortSchedulePage";
import HouseholdSettingsPage from "../pages/settings/HouseholdSettingsPage";
import ManageAreasPage from "../pages/settings/ManageAreasPage";
import MembersPage from "../pages/settings/MembersPage";
import AppSettingsPage from "../pages/settings/AppSettingsPage";
import DeveloperToolsPage from "../pages/settings/DeveloperToolsPage";
import AiAssistantsPage from "../pages/settings/AiAssistantsPage";
import CreateTaskPage from "../pages/CreateTaskPage";
import TaskDetailPage from "../pages/TaskDetailPage";
import CreateAreaPage from "../pages/CreateAreaPage";
import { useStore } from "../store/useStore";

/** Tab icon that switches to a filled/bolder look when selected. */
function TabIcon({ icon: Icon, selected }: { icon: LucideIcon; selected: boolean }) {
  return (
    <Icon
      aria-hidden="true"
      className="w-6 h-6"
      strokeWidth={selected ? 2.4 : 2}
      fill={selected ? "currentColor" : "none"}
      fillOpacity={selected ? 0.18 : 0}
    />
  );
}

export default function TabsLayout() {
  const location = useLocation();
  const currentHouseholdId = useStore((state) => state.currentHouseholdId);
  const households = useStore((state) => state.households);
  const userHouseholds = useStore((state) => state.userHouseholds);
  const user = useStore((state) => state.user);

  // Get current household's gamification setting
  const currentHousehold = households.find(
    (h) => h.uuid === currentHouseholdId
  );
  const isGamificationEnabled = currentHousehold?.gamification_enabled ?? true;

  // Get current user's role in the household
  const currentUserHousehold = userHouseholds.find(
    (uh) =>
      uh.household_uuid === currentHouseholdId &&
      uh.user_uuid === user?.id
  );
  const isCleaner = currentUserHousehold?.role === "cleaner";

  // Home tab stays highlighted while drilling into areas/tasks
  const isHomeActive =
    location.pathname === "/home" ||
    location.pathname.startsWith("/areas/");
  const isTodayActive = location.pathname === "/today";
  const isLeaderboardActive = location.pathname === "/leaderboard";
  const isSettingsActive = location.pathname.startsWith("/settings");

  // Cleaners don't get the Home overview, so their default is the task list
  const defaultRoute = isCleaner ? "/today" : "/home";

  return (
    <IonTabs>
      <IonRouterOutlet>
        <Switch>
          <Route path="/today" exact component={TodayPage} />
          <Route path="/home" exact component={HomeStatusPage} />
          <Route
            path="/areas/:areaId/tasks/create"
            exact
            component={CreateTaskPage}
          />
          <Route
            path="/areas/:areaId/tasks/:taskId"
            exact
            component={TaskDetailPage}
          />
          <Route path="/areas/create" exact component={CreateAreaPage} />
          <Route path="/areas/:areaId" exact component={AreaDetailPage} />
          <Route path="/leaderboard" exact component={LeaderboardPage} />
          <Route path="/settings/effort-schedule" exact component={EffortSchedulePage} />
          <Route path="/settings/household" exact component={HouseholdSettingsPage} />
          <Route path="/settings/areas" exact component={ManageAreasPage} />
          <Route path="/settings/members" exact component={MembersPage} />
          <Route path="/settings/app" exact component={AppSettingsPage} />
          <Route path="/settings/developer" exact component={DeveloperToolsPage} />
          <Route path="/settings/ai-assistants" exact component={AiAssistantsPage} />
          <Route path="/settings" exact component={SettingsPage} />
          {/* Legacy redirect */}
          <Route path="/household" exact>
            <Redirect to="/home" />
          </Route>
          <Route path="/" exact>
            <Redirect to={defaultRoute} />
          </Route>
        </Switch>
      </IonRouterOutlet>

      <IonTabBar slot="bottom">
        {!isCleaner && (
          <IonTabButton tab="home" href="/home" selected={isHomeActive}>
            <TabIcon icon={House} selected={isHomeActive} />
            <IonLabel>Home</IonLabel>
          </IonTabButton>
        )}

        <IonTabButton tab="today" href="/today" selected={isTodayActive}>
          <TabIcon icon={ListChecks} selected={isTodayActive} />
          <IonLabel>Task List</IonLabel>
        </IonTabButton>

        {!isCleaner && isGamificationEnabled && (
          <IonTabButton
            tab="leaderboard"
            href="/leaderboard"
            selected={isLeaderboardActive}
          >
            <TabIcon icon={Trophy} selected={isLeaderboardActive} />
            <IonLabel>Leaderboard</IonLabel>
          </IonTabButton>
        )}

        <IonTabButton tab="settings" href="/settings" selected={isSettingsActive}>
          <TabIcon icon={Settings} selected={isSettingsActive} />
          <IonLabel>Settings</IonLabel>
        </IonTabButton>
      </IonTabBar>
    </IonTabs>
  );
}
